import React, { FC, useMemo, useRef, useState } from 'react';

import { isNull, isUndefined } from 'lodash';

import { PosProductLocationInventory } from '@sparkplug/lib';

import { TableProvider } from '@contexts/TableContext';

import { BookIcon, Info } from '@components/icons';
import Tooltip from '@components/layout/Tooltip';
import Table from '@components/table/Table';
import Toolbar from '@components/toolbar/Toolbar';

import { useSearch } from '@hooks/UIHooks';

import { openUrl } from '@helpers/ui';

import { IPosLocation, IPosProduct } from '@app/types/PosTypes';
import { THeadCell } from '@app/types/TableTypes';
import { IOption } from '@app/types/UITypes';

import './SparkInventoryChart.scss';

export interface ProductInventoryRow extends Omit<IPosProduct, 'inventory'> {
    key: string;
    inventory: PosProductLocationInventory;
    locationId?: string;
}

const NotApplicableLabel = () => (
    <div className="not-applicable">
        N/A
        <Tooltip
            placement="right"
            title="Unable to retrieve the inventory information for this product at this time. This does not imply the remaining inventory is 0."
        >
            <Info fontSize="small" />
        </Tooltip>
    </div>
);
interface InventoryToolbarProps {
    showLocationsDropdown: boolean;
    outOfStock: boolean;
    searchFilter: string;
    onChangeSearchFilter: (event: any) => void;
    toggleOutOfStock: (outOfStock: boolean) => void;
    activeLocationValue: string;
    chartLocationOptions: IOption[];
    onUpdateChartSettings: (updatedSettings: { locationIds: string[] }) => void;
}

export const SparkDetailsInventoryToolbar: FC<InventoryToolbarProps> = ({
    showLocationsDropdown,
    activeLocationValue,
    onUpdateChartSettings,
    chartLocationOptions,
    outOfStock,
    toggleOutOfStock,
    searchFilter,
    onChangeSearchFilter,
}) => {
    return (
        <Toolbar className="inventory-toolbar">
            {showLocationsDropdown && (
                <Toolbar.Dropdown
                    label={null}
                    value={activeLocationValue}
                    onSelect={(locationIds: string) =>
                        onUpdateChartSettings({
                            locationIds: locationIds.split(','),
                        })
                    }
                    options={chartLocationOptions}
                />
            )}
            <Toolbar.Search
                name="sparks-search"
                defaultValue={searchFilter}
                onChange={onChangeSearchFilter}
            />
            <Toolbar.Checkbox
                label="Show only out-of-stock"
                value={outOfStock}
                onChange={() => toggleOutOfStock(!outOfStock)}
            />

            <Toolbar.Button
                className="read-more-button"
                alignEnd
                variant="flat"
                color="blue"
                startIcon={<BookIcon />}
                onClick={() =>
                    openUrl(
                        'https://help.sparkplug.app/en/articles/7856282-learn-more-about-inventory-tracking',
                    )
                }
            >
                Learn more about inventory
            </Toolbar.Button>
        </Toolbar>
    );
};
interface InventoryChartProps {
    locations: IPosLocation[];
    products: IPosProduct[];
    showLocationsDropdown: boolean;
}
const SparkInventoryChart: FC<InventoryChartProps> = ({
    locations,
    products,
    showLocationsDropdown,
}) => {
    const headCells: THeadCell<ProductInventoryRow>[] = [
        {
            id: 'name',
            sortType: 'string',
            label: 'Product',
            render: (row) => {
                return (
                    <Table.Cell className="product_name">
                        <div>{row.name}</div>
                    </Table.Cell>
                );
            },
        },
        {
            id: 'location',
            label: 'Location',
            render: (row) => {
                const location = locations.find((loc) => loc.value === row.locationId);
                return (
                    <Table.Cell className="location_name">
                        <div>{location ? location?.label : '--'}</div>
                    </Table.Cell>
                );
            },
        },
        {
            id: 'inventory.quantityAvailable',
            sortType: 'numeric',
            label: 'Remaining Inventory',
            render: (row) => {
                const quantityAvailable = row.inventory.quantityAvailable;
                const isNotApplicableValue =
                    quantityAvailable < 0 ||
                    isUndefined(quantityAvailable) ||
                    isNull(quantityAvailable);
                return (
                    <Table.Cell className="remaining">
                        <div className={isNotApplicableValue ? 'not-applicable' : ''}>
                            {isNotApplicableValue ? (
                                <NotApplicableLabel />
                            ) : (
                                row.inventory.quantityAvailable
                            )}
                        </div>
                    </Table.Cell>
                );
            },
        },
    ];
    const inventoryChartRef = useRef(null);
    const [outOfStock, setOutOfStock] = useState(false);
    const [selectedLocations, setSelectedLocations] = useState<{ locationIds: string[] }>({
        locationIds: [],
    });

    const productsByLocation = useMemo(() => {
        const location = locations && locations.length === 1 ? locations[0].value : undefined;
        const productsByLoc: ProductInventoryRow[] = products.flatMap((product) => {
            if (!product.inventory) {
                return {
                    ...product,
                    locationId: location,
                    inventory: {
                        quantityAvailable: Number.MIN_SAFE_INTEGER,
                        unit: '',
                        updatedAt: new Date(),
                    },
                    key: `${product._id}unknown`,
                };
            }

            return Object.entries(product.inventory).map(([locationId, inventory]) => {
                let quantity = inventory.quantityAvailable;
                if (typeof inventory.quantityAvailable !== 'number') {
                    quantity = Number.isNaN(parseFloat(inventory.quantityAvailable))
                        ? Number.MIN_SAFE_INTEGER
                        : parseFloat(inventory.quantityAvailable);
                }
                const map = {
                    ...product,
                    locationId,
                    inventory: { ...inventory, quantityAvailable: quantity },
                    key: product._id + locationId,
                };
                return map;
            });
        });

        return productsByLoc;
    }, [products, locations]);

    const hasMissingLocations = useMemo(() => {
        return productsByLocation.some((product) => !product.locationId);
    }, [productsByLocation]);

    const onUpdateChartSettings = (updatedSettings: { locationIds: string[] }) => {
        setSelectedLocations(updatedSettings);
    };

    const activeLocations = useMemo(() => {
        if (selectedLocations.locationIds.length === 0) {
            return locations;
        }
        if (
            selectedLocations.locationIds.length === 1 &&
            selectedLocations.locationIds[0] === '*'
        ) {
            return locations;
        }
        return locations.filter(({ value }) => selectedLocations.locationIds.includes(value));
    }, [selectedLocations.locationIds, locations]);

    const activeLocationValue = useMemo(() => {
        if (!activeLocations) {
            return '';
        }
        if (activeLocations.length === locations.length) {
            return '*';
        }
        return activeLocations.map(({ value }) => value).join(',');
    }, [activeLocations]);

    const chartLocationOptions = useMemo<IOption[]>(() => {
        const chartLocations = [
            {
                value: '*',
                label: `All ${locations.length} Locations`,
            },
            ...locations,
        ];
        if (hasMissingLocations) {
            chartLocations.push({ value: '', label: 'Missing Location' });
        }
        return chartLocations;
    }, [locations, hasMissingLocations]);
    const applyOutOfStockFilter = (rows: ProductInventoryRow[]) => {
        if (outOfStock) {
            return rows.filter((product) => product.inventory.quantityAvailable === 0);
        } else {
            return rows;
        }
    };
    const applyLocationFilter = (rows: ProductInventoryRow[]) => {
        if (activeLocationValue === '*') {
            return rows;
        }
        if (!activeLocationValue) {
            return rows.filter((product) => !product.locationId);
        }
        const activeLocationIds = activeLocationValue.split(',');
        return rows.filter(
            ({ locationId }) => locationId && activeLocationIds.includes(locationId),
        );
    };

    const { searchFilter, onChangeSearchFilter, applySearch } = useSearch(['name']);
    const toggleOutOfStock = (OOStock: boolean) => {
        setOutOfStock(OOStock);
    };

    return (
        <>
            <SparkDetailsInventoryToolbar
                {...{
                    showLocationsDropdown,
                    outOfStock,
                    toggleOutOfStock,
                    activeLocationValue,
                    onUpdateChartSettings,
                    chartLocationOptions,
                    onChangeSearchFilter,
                    searchFilter,
                }}
            />
            <div className="spark-inventory-chart" ref={inventoryChartRef}>
                <TableProvider
                    showPagination={false}
                    headCells={headCells}
                    rows={productsByLocation}
                    filters={[applySearch, applyLocationFilter, applyOutOfStockFilter]}
                    defaultOptions={{
                        orderBy: 'inventory.quantityAvailable',
                        order: 'desc',
                    }}
                >
                    <Table useInfiniteScrollStyling useExternalProvider variant="smooth">
                        <Table.RenderHead />
                        <Table.RenderBody
                            emptyStateText="No products with these filters"
                            dynamicHeight={{
                                infiniteScroll: true,
                                containerRef: inventoryChartRef,
                                scrollToTopElement: {
                                    getText: (itemCount) => `Viewing ${itemCount} products`,
                                },
                            }}
                            highlightRowOnHover
                        />
                    </Table>
                </TableProvider>
            </div>
        </>
    );
};

export default SparkInventoryChart;
